import { create } from "zustand";
import {
  IBuildType,
  INotificationBaseMessage,
  INotificationBaseRelatedToShow,
  IPayloadUpdatedBuildTypeLibrary,
  NotificationType,
} from "@evennt-tech/api-v2-core";
import { apiV2 } from "api/firebase";
import { devtools } from "zustand/middleware";
import { IdleManager } from "tools/idle";
import { registerStore } from "store/utils/reset-store.utils";
import {
  createRequestSeq,
  languageCodeQuery,
  resolveCacheLanguage,
} from "store/utils/localised-cache.utils";
import {
  IGetManyHiddenBuildTypesForProductsBodyReq,
  IGetManyHiddenBuildTypesForProductsRes,
} from "@evennt-tech/api-v2-external";

type State = {
  items: Record<string, Array<IBuildType>>;
  language: Record<string, string>;
  isLoading: boolean;
  hiddenForProducts: IGetManyHiddenBuildTypesForProductsRes;
  isHiddenForProductsLoading: boolean;
};

let hiddenForProductsRequestId = 0;

type Action = {
  fetch: (
    showId: string,
    force?: boolean,
    languageCode?: string
  ) => Promise<void>;
  update: (showId: string, languageCode?: string) => Promise<void>;
  autoCatch: (showId: string, organiserCompanyId?: string) => Promise<void>;
  getByShowId: (showId: string) => Array<IBuildType>;
  fetchHiddenForProducts: (
    args: IGetManyHiddenBuildTypesForProductsBodyReq
  ) => Promise<void>;
  clearHiddenForProducts: () => void;
};

const nextRequest = createRequestSeq();

export const useBuildTypes = create(
  devtools<State & Action>((set, get) => ({
    items: {},
    language: {},
    isLoading: false,
    showId: null,
    hiddenForProducts: [],
    isHiddenForProductsLoading: false,

    fetchHiddenForProducts: async (body) => {
      hiddenForProductsRequestId += 1;
      const requestId = hiddenForProductsRequestId;
      const isStale = () => requestId !== hiddenForProductsRequestId;

      set(() => ({ isHiddenForProductsLoading: true }));

      try {
        const hiddenForProducts = await apiV2.buildType.getManyHiddenForProducts(
          body
        );

        if (isStale()) return;

        set(() => ({ hiddenForProducts }));
      } catch (e) {
        console.error("build-type store, hidden for products:", e);

        if (isStale()) return;

        set(() => ({ hiddenForProducts: [] }));
      } finally {
        if (!isStale()) {
          set(() => ({ isHiddenForProductsLoading: false }));
        }
      }
    },

    clearHiddenForProducts: () => {
      hiddenForProductsRequestId += 1;

      set(() => ({ hiddenForProducts: [], isHiddenForProductsLoading: false }));
    },

    getByShowId: (showId: string): Array<IBuildType> => {
      const { items } = get();

      if (!items?.[showId]) {
        return [];
      }

      return items[showId];
    },

    autoCatch: async (showId: string, organiserCompanyId?: string) => {
      const { update } = get();
      IdleManager.getInstance().addSubscriber(
        `build-types-${showId}`,
        async () => {
          await update(showId);
        }
      );

      apiV2.notification.subscribe<INotificationBaseRelatedToShow>({
        key: NotificationType.CREATED_BUILD_TYPE,
        showId,
        callback: async ({ showId }) => {
          await update(showId);
        },
      });

      apiV2.notification.subscribe<INotificationBaseRelatedToShow>({
        key: NotificationType.DELETE_BUILD_TYPE,
        showId,
        callback: async ({ showId }) => {
          await update(showId);
        },
      });

      apiV2.notification.subscribe<INotificationBaseRelatedToShow>({
        key: NotificationType.UPDATED_BUILD_TYPE,
        showId,
        callback: async ({ showId }) => {
          await update(showId);
        },
      });

      if (organiserCompanyId) {
        apiV2.notification.subscribe<
          INotificationBaseMessage<IPayloadUpdatedBuildTypeLibrary>
        >({
          key: NotificationType.UPDATED_BUILD_TYPE_LIBRARY,
          listenerId: `build-type-library-${showId}`,
          callback: async ({ payload }) => {
            if (payload?.organiserCompanyId === organiserCompanyId) {
              await update(showId);
            }
          },
        });
      }
    },

    update: async (showId: string, languageCode?: string) => {
      const { items, language } = get();
      const nextLanguage = resolveCacheLanguage(language, showId, languageCode);
      const isLatestRequest = nextRequest(showId);

      try {
        const buildTypes = await apiV2.buildType.getMany({
          showId,
          ...languageCodeQuery(nextLanguage),
        });

        if (!isLatestRequest()) {
          return;
        }

        set(() => ({
          items: {
            ...items,

            [showId]: buildTypes,
          },
          language: { ...language, [showId]: nextLanguage },
        }));
      } catch (e) {
        console.error("build-type store:", e);
      }
    },
    fetch: async (showId: string, force = false, languageCode?: string) => {
      const { isLoading, items, language, update } = get();
      const nextLanguage = resolveCacheLanguage(language, showId, languageCode);

      if (!force && language[showId] === nextLanguage) {
        if (items[showId] || isLoading) {
          return;
        }
      }

      set(() => ({ isLoading: true }));

      try {
        await update(showId, nextLanguage);
      } catch (e) {
        console.error(e);
      } finally {
        set(() => ({ isLoading: false, showId }));
      }
    },
  }))
);

registerStore(useBuildTypes);
